import type { PaymentMethodListResponseDto } from '@saaga/billingv2-sdk'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import type { BillingPaymentCard } from '@/components/billing/billing.interface'
import { transformPaymentMethodToCard } from '@/utils/payment-method.util'

import { useBillingClient } from './use-billing-client.hook'
import { usePaymentMethodCheckout } from './use-payment-method-checkout.hook'

export interface UsePaymentMethodsReturn {
  cardList: BillingPaymentCard[]
  isLoading: boolean
  error: string | null
  setDefaultCard: (cardId: string) => Promise<boolean>
  deleteCard: (cardId: string) => Promise<boolean>
  addCard: () => Promise<void>
  isSettingDefault: boolean
  isDeleting: boolean
  isAddingCard: boolean
  refetch: () => Promise<void>
}

const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback

const updateCardListDefault = (
  cardList: BillingPaymentCard[],
  cardId: string
): BillingPaymentCard[] => cardList.map((c) => ({ ...c, isDefault: c.id === cardId }))

const removeCardFromList = (cardList: BillingPaymentCard[], cardId: string): BillingPaymentCard[] =>
  cardList.filter((c) => c.id !== cardId)

const usePaymentMethodsState = () => {
  const [cardList, setCardList] = useState<BillingPaymentCard[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSettingDefault, setIsSettingDefault] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  return {
    cardList,
    setCardList,
    isLoading,
    setIsLoading,
    error,
    setError,
    isSettingDefault,
    setIsSettingDefault,
    isDeleting,
    setIsDeleting,
  }
}

const useFetchPaymentMethods = (
  getPaymentMethods: () => Promise<PaymentMethodListResponseDto>,
  state: ReturnType<typeof usePaymentMethodsState>
) => {
  const { setIsLoading, setError, setCardList } = state

  const fetchPaymentMethods = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      setCardList((await getPaymentMethods()).paymentMethods.map(transformPaymentMethodToCard))
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load payment methods'))
      logger.error('Failed to fetch payment methods', { error: err })
    } finally {
      setIsLoading(false)
    }
  }, [getPaymentMethods, setIsLoading, setError, setCardList])

  useEffect(() => {
    void fetchPaymentMethods()
  }, [fetchPaymentMethods])

  return fetchPaymentMethods
}

const useCardOperations = (
  billingClient: ReturnType<typeof useBillingClient>,
  state: ReturnType<typeof usePaymentMethodsState>
) => {
  const setDefaultCard = useCallback(
    async (cardId: string): Promise<boolean> => {
      state.setIsSettingDefault(true)
      try {
        await billingClient.setDefaultPaymentMethod(cardId)
        state.setCardList((prev) => updateCardListDefault(prev, cardId))
        toast.success('Default payment method updated')
        trackEvent('payment_method_set_default', {})
        return true
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to set default card'))
        logger.error('Failed to set default payment method', { error: err, cardId })
        return false
      } finally {
        state.setIsSettingDefault(false)
      }
    },
    [billingClient, state]
  )

  const deleteCard = useCallback(
    async (cardId: string): Promise<boolean> => {
      state.setIsDeleting(true)
      try {
        await billingClient.deletePaymentMethod(cardId)
        state.setCardList((prev) => removeCardFromList(prev, cardId))
        toast.success('Payment method removed')
        trackEvent('payment_method_deleted', {})
        return true
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to delete card'))
        logger.error('Failed to delete payment method', { error: err, cardId })
        return false
      } finally {
        state.setIsDeleting(false)
      }
    },
    [billingClient, state]
  )

  return { setDefaultCard, deleteCard }
}

export const usePaymentMethods = (
  onClientSecret: (clientSecret: string) => void
): UsePaymentMethodsReturn => {
  const billingClient = useBillingClient()
  const { initiateCheckout, isLoading: isAddingCard } = usePaymentMethodCheckout(
    billingClient.setupPaymentMethodCheckout,
    onClientSecret
  )
  const state = usePaymentMethodsState()
  const fetchPaymentMethods = useFetchPaymentMethods(billingClient.getPaymentMethods, state)
  const { setDefaultCard, deleteCard } = useCardOperations(billingClient, state)

  return {
    cardList: state.cardList,
    isLoading: state.isLoading,
    error: state.error,
    setDefaultCard,
    deleteCard,
    addCard: initiateCheckout,
    isSettingDefault: state.isSettingDefault,
    isDeleting: state.isDeleting,
    isAddingCard,
    refetch: fetchPaymentMethods,
  }
}
